import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Contact,
  Globe,
  Shield,
  Settings,
  MessageSquare,
  CreditCard,
  BarChart3,
  Briefcase,
  PhoneCall,
  MessageCircle,
  Brain,
  UserCircle,
  Database,
  ChevronLeft,
  ChevronRight,
  PanelLeftClose,
  PanelLeft,
  Calendar,
  FileSearch,
  ClipboardList
} from 'lucide-react';

// Navigation items by role
const getNavItems = (role, t) => {
  const ownerItems = [
    { id: 'owner-dashboard', path: '/', label: t('dashboard'), icon: BarChart3, description: t('analyticsAndKpis') || 'Analytics & KPIs' },
    { id: 'leads', path: '/leads', label: t('allLeads'), icon: Users, description: t('manageLeads') || 'Manage leads' },
    { id: 'call-list', path: '/call-list', label: t('callList'), icon: PhoneCall, description: t('scheduledCalls') || 'Scheduled calls' },
    { id: 'contacts', path: '/contacts', label: t('contacts'), icon: Contact, description: t('contactDirectory') || 'Contact directory' },
    { id: 'calendar', path: '/calendar', label: t('bookingCalendar') || 'Booking Calendar', icon: Calendar, description: t('scheduledEvents') || 'Scheduled events' },
    { id: 'conversation-logs', path: '/conversation-logs', label: t('conversations'), icon: MessageCircle, description: t('whatsappHistory') || 'WhatsApp history' },
    { id: 'greetings-log', path: '/greetings-log', label: t('greetingsLog') || 'Greetings Log', icon: ClipboardList, description: t('whatsappAutoGreetingsLog') || 'Auto-greetings audit trail' },
    { id: 'finance', path: '/finance', label: t('finance'), icon: CreditCard, description: t('paymentsAndInvoicing') || 'Payments & invoicing' },
    { id: 'audit-logs', path: '/audit-logs', label: t('auditLogs') || 'Audit Logs', icon: FileSearch, description: t('viewSystemAuditLogs') || 'Secure system audit trail' }
  ];

  const salesItems = [
    { id: 'employee', path: '/employee', label: t('dashboard'), icon: LayoutDashboard, description: t('analyticsAndKpis') || 'Analytics & KPIs' },
    { id: 'leads', path: '/leads', label: t('myLeads'), icon: Users, description: 'My leads pipeline' },
    { id: 'call-list', path: '/call-list', label: t('callList'), icon: PhoneCall, description: t('scheduledCalls') || 'Scheduled calls' },
    { id: 'contacts', path: '/contacts', label: t('contacts'), icon: Contact, description: t('contactDirectory') || 'Contact directory' },
    { id: 'calendar', path: '/calendar', label: t('bookingCalendar') || 'Booking Calendar', icon: Calendar, description: t('scheduledEvents') || 'Scheduled events' },
    { id: 'conversation-logs', path: '/conversation-logs', label: t('conversations'), icon: MessageCircle, description: t('whatsappHistory') || 'WhatsApp history' },
    { id: 'greetings-log', path: '/greetings-log', label: t('greetingsLog') || 'Greetings Log', icon: ClipboardList, description: t('whatsappAutoGreetingsLog') || 'Auto-greetings audit trail' },
  ];

  const financeItems = [
    { id: 'finance', path: '/finance', label: t('finance'), icon: CreditCard, description: t('paymentsAndInvoicing') || 'Payments & invoicing' },
    { id: 'calendar', path: '/calendar', label: t('bookingCalendar') || 'Booking Calendar', icon: Calendar, description: t('scheduledEvents') || 'Scheduled events' }
  ];

  switch (role) {
    case 'Owner':
    case 'Admin':
      return ownerItems;
    case 'Sales':
      return salesItems;
    case 'Finance':
      return financeItems;
    default:
      return ownerItems;
  }
};

import { useLanguage } from '../../contexts/LanguageContext';
import { useLocation } from 'react-router-dom';
import { whatsappService } from '../../services/api';
import { useSocket } from '../../hooks/useSocket';

export default function RoleSidebar({
  activeTab,
  setActiveTab,
  userRole,
  onShowAdmin,
  onShowIntegrations,
  onShowUserSettings,
  onShowMigration,
  isMobile = false,
  collapsed = false,
  onToggleCollapse
}) {
  const { t } = useLanguage();
  const location = useLocation();
  const { socket } = useSocket();
  const navItems = getNavItems(userRole, t);
  const isOwnerOrAdmin = ['Owner', 'Admin'].includes(userRole);

  const [hasUnreadChats, setHasUnreadChats] = useState(false);

  const checkUnreadChats = async () => {
    try {
      const data = await whatsappService.getConversations();
      const hasUnread = data.some(l => l.hasUnreadMessages);
      setHasUnreadChats(hasUnread);
    } catch (err) {
      console.error('Error fetching unread status for sidebar', err);
    }
  };

  useEffect(() => {
    checkUnreadChats();
  }, []);

  useEffect(() => {
    if (!socket) return;
    socket.on('activity_added', checkUnreadChats);
    socket.on('lead_created', checkUnreadChats);
    socket.on('chat_read', checkUnreadChats);
    return () => {
      socket.off('activity_added', checkUnreadChats);
      socket.off('lead_created', checkUnreadChats);
      socket.off('chat_read', checkUnreadChats);
    };
  }, [socket]);

  const handleNavClick = () => {
    if (isMobile && setActiveTab) {
      setActiveTab();
    }
  };

  // Mobile layout - simplified dropdown menu
  if (isMobile) {
    return (
      <div className="p-2">
        {/* Navigation Grid */}
        <div className="grid grid-cols-2 gap-2">
          {navItems.map((item) => (
            <NavLink
              key={item.id}
              to={item.path}
              onClick={handleNavClick}
              className={({ isActive }) => `flex items-center gap-2 px-3 py-3 rounded-xl text-left transition-all ${isActive
                ? 'bg-blue-100 text-blue-700 font-semibold'
                : 'bg-gray-50 text-gray-600 active:bg-gray-100'
                }`}
            >
              {({ isActive }) => (
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="relative">
                    <item.icon
                      className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-400'
                        }`}
                    />
                    {item.id === 'conversation-logs' && hasUnreadChats && (
                      <span className="absolute -top-5 -left-5 w-3 h-3 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>
                    )}
                  </div>
                  <span className="text-sm font-medium truncate">{item.label}</span>
                </div>
              )}
            </NavLink>
          ))}
        </div>

        {/* Admin Section */}
        {isOwnerOrAdmin && (
          <>
            <div className="my-3 border-t border-gray-200"></div>
            <div className="grid grid-cols-2 gap-2">
              <NavLink
                to="/sources"
                onClick={handleNavClick}
                className={({ isActive }) => `flex items-center gap-2 px-3 py-3 rounded-xl text-left transition-all ${isActive
                  ? 'bg-blue-100 text-blue-700 font-semibold'
                  : 'bg-gray-50 text-gray-600 active:bg-gray-100'
                  }`}
              >
                {({ isActive }) => (
                  <>
                    <Globe className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-blue-500' : 'text-gray-400'}`} />
                    <span className="text-sm font-medium truncate">{t('sources') || 'Sources'}</span>
                  </>
                )}
              </NavLink>
              <NavLink
                to="/admin"
                onClick={handleNavClick}
                className={({ isActive }) => `flex items-center gap-2 px-3 py-3 rounded-xl text-left transition-all ${isActive
                  ? 'bg-purple-100 text-purple-700 font-semibold'
                  : 'bg-purple-50 text-purple-700 active:bg-purple-100'
                  }`}
              >
                <Shield className="w-5 h-5 text-purple-500" />
                <span className="text-sm font-medium">{t('adminControlPanel')?.split(' ')[0] || 'Admin'}</span>
              </NavLink>
            </div>
          </>
        )}

        {/* User Settings */}
        <div className="my-3 border-t border-gray-200"></div>
        <button
          onClick={onShowUserSettings}
          className="w-full flex items-center gap-2 px-3 py-3 rounded-xl text-left bg-gray-100 text-gray-700 active:bg-gray-200"
        >
          <UserCircle className="w-5 h-5 text-gray-500" />
          <span className="text-sm font-medium">{t('myAccount') || 'My Account'}</span>
        </button>
      </div>
    );
  }

  // Desktop layout - collapsible sidebar
  return (
    <aside className="w-full h-full bg-white flex flex-col relative">
      {/* Toggle Button */}
      <div className="absolute top-6 -right-4 z-50">
        <button
          onClick={onToggleCollapse}
          className="group flex items-center justify-center w-8 h-8 !min-h-[32px] rounded-full bg-white border border-gray-200 text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 shadow-sm"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4 transition-transform duration-200 group-hover:scale-110" />
          ) : (
            <ChevronLeft className="w-4 h-4 transition-transform duration-200 group-hover:scale-110" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className={`flex-1 ${collapsed ? 'px-2 py-3' : 'p-4'} space-y-1 overflow-y-auto overflow-x-hidden`}>
        {navItems.map((item) => (
          <NavLink
            key={item.id}
            to={item.path}
            className={({ isActive }) => `
              group relative w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3
              ${collapsed ? 'px-0 py-3' : 'px-4 py-3'} rounded-xl text-left transition-all duration-200
              ${isActive
                ? 'bg-blue-50 text-blue-700 font-semibold shadow-sm'
                : 'text-gray-600 hover:bg-gray-50'
              }`}
          >
            {({ isActive }) => (
              <>
                <div className="relative">
                  <item.icon
                    className={`w-5 h-5 flex-shrink-0 transition-colors duration-200 ${isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'
                      }`}
                  />
                  {item.id === 'conversation-logs' && hasUnreadChats && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>
                  )}
                </div>
                {!collapsed && (
                  <div className="min-w-0 overflow-hidden flex-1 flex justify-between items-center pr-2">
                    <div>
                      <p className="text-sm font-medium truncate whitespace-nowrap">{item.label}</p>
                      {!isActive && (
                        <p className="text-[10px] text-gray-400 truncate whitespace-nowrap">{item.description}</p>
                      )}
                    </div>
                  </div>
                )}
                {/* Tooltip when collapsed */}
                {collapsed && (
                  <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-gray-800 text-white text-xs rounded-lg 
                    opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 
                    pointer-events-none whitespace-nowrap z-50 shadow-lg">
                    {item.label}

                  </div>
                )}
              </>
            )}
          </NavLink>
        ))}

        {/* Admin Section */}
        {isOwnerOrAdmin && (
          <>
            <div className="my-4 border-t border-gray-100"></div>
            {!collapsed && (
              <p className="px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 whitespace-nowrap overflow-hidden">
                {t('settings') || 'Settings'}
              </p>
            )}

            {/* Sources */}
            <NavLink
              to="/sources"
              className={({ isActive }) => `
                group relative w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3
                ${collapsed ? 'px-0 py-3' : 'px-4 py-3'} rounded-xl text-left transition-all duration-200
                ${isActive
                  ? 'bg-blue-50 text-blue-700 font-semibold shadow-sm'
                  : 'text-gray-600 hover:bg-gray-50'
                }`}
            >
              {({ isActive }) => (
                <>
                  <Globe className={`w-5 h-5 flex-shrink-0 transition-colors duration-200 ${isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                  {!collapsed && (
                    <div className="min-w-0 overflow-hidden">
                      <p className="text-sm font-medium truncate whitespace-nowrap">{t('sources') || 'Sources'}</p>
                      {!isActive && <p className="text-[10px] text-gray-400 truncate whitespace-nowrap">{t('leadSources') || 'Lead sources'}</p>}
                    </div>
                  )}
                  {collapsed && (
                    <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-gray-800 text-white text-xs rounded-lg 
                      opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 
                      pointer-events-none whitespace-nowrap z-50 shadow-lg">
                      {t('sources') || 'Sources'}

                    </div>
                  )}
                </>
              )}
            </NavLink>

            {/* Admin Panel */}
            <NavLink
              to="/admin"
              className={({ isActive }) => `
                group relative w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3
                ${collapsed ? 'px-0 py-3' : 'px-4 py-3'} rounded-xl text-left transition-all duration-200
                ${isActive || location.pathname === '/admin'
                  ? 'bg-purple-50 text-purple-700 font-semibold shadow-sm'
                  : 'text-gray-600 hover:bg-purple-50 hover:text-purple-700'
                }`}
            >
              {({ isActive }) => {
                const isCurrent = isActive || location.pathname === '/admin';
                return (
                  <>
                    <Shield className={`w-5 h-5 flex-shrink-0 transition-colors duration-200 ${isCurrent ? 'text-purple-600' : 'text-purple-500'}`} />
                    {!collapsed && (
                      <div className="min-w-0 overflow-hidden">
                        <p className="text-sm font-medium truncate whitespace-nowrap">{t('adminControlPanel') || 'Admin Panel'}</p>
                        {!isCurrent && <p className="text-[10px] text-gray-400 truncate whitespace-nowrap">{t('usersAndPermissions') || 'Users & permissions'}</p>}
                      </div>
                    )}
                    {collapsed && (
                      <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-gray-800 text-white text-xs rounded-lg 
                        opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 
                        pointer-events-none whitespace-nowrap z-50 shadow-lg">
                        {t('adminControlPanel') || 'Admin Panel'}
                      </div>
                    )}
                  </>
                );
              }}
            </NavLink>

          </>
        )}
      </nav>

      {/* User Account */}
      <div className={`${collapsed ? 'p-2' : 'p-4'} border-t`}>
        <button
          onClick={onShowUserSettings}
          className={`group relative w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3 
            ${collapsed ? 'px-0 py-3' : 'px-4 py-3'} rounded-xl text-left text-gray-600 hover:bg-gray-100 transition-all duration-200`}
        >
          <UserCircle className="w-5 h-5 text-gray-500 flex-shrink-0" />
          {!collapsed && (
            <div className="min-w-0 overflow-hidden">
              <p className="text-sm font-medium truncate whitespace-nowrap">{t('myAccount') || 'My Account'}</p>
              <p className="text-[10px] text-gray-400 truncate whitespace-nowrap">{t('profileAndPassword') || 'Profile & password'}</p>
            </div>
          )}
          {collapsed && (
            <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-gray-800 text-white text-xs rounded-lg 
              opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 
              pointer-events-none whitespace-nowrap z-50 shadow-lg">
              {t('myAccount') || 'My Account'}
            </div>
          )}
        </button>
      </div>
    </aside>
  );
}
